import {Breadcrumb, BreadcrumbHint} from "@sentry/browser";
import * as Sentry from "@sentry/browser";
import {
  ApiError,
  Checkout,
  Customer,
  ICartMessage,
  IPaymentTransaction,
  IGetCheckoutShippingOptionsData,
} from "../typescript-library";
import {IBraintreeError} from "../components/checkout/steps/Summary/summaryErrors";
import {CheckoutStep} from "../components/checkout/Checkout";
import {SentryXhrData} from "@sentry/core";
import {Payments} from "../components/lib/payments/PaymentController";

type TransactionCategoryType = "createPayment" | "initPayment" | "authorizePayment" | "initBraintreeClient";
type CheckoutMetaDataType = {
  logisticsType: number | null;
  paymentType: number | null;
};

/**
 * breadcrumb preparation and addition of information
 * used on sentry init in 'index.tsx'
 * @param breadcrumb
 * @param hint
 */
export const beforeBreadcrumb = (
  breadcrumb: Breadcrumb,
  hint?: {
    xhr?: XMLHttpRequest & {
      __sentry_xhr_v3__: SentryXhrData | undefined;
    };
    response: Response;
    input: [string, {method?: string | undefined; body: string}];
  } & BreadcrumbHint,
): Breadcrumb => {
  // if url contains "auth" substring -> replace it
  if (breadcrumb.data?.url) {
    breadcrumb.data.url = authReplacer(breadcrumb.data.url as string);
  }

  if (!hint) {
    return breadcrumb;
  }

  if (hint.response instanceof Response) {
    const {input} = hint;
    const method = input[1]?.method ?? "get";
    if (
      method.toLowerCase() !== "get" &&
      Array.isArray(input) &&
      input.length > 1 &&
      (!hint.response.ok || input[0].includes("/bulk"))
    ) {
      let data: any;
      try {
        data = JSON.parse(authReplacer(input[1].body));
      } catch (e) {
        data = Object.fromEntries(new URLSearchParams(authReplacer(input[1].body)).entries());
      }
      Sentry.addBreadcrumb({
        level: "debug",
        category: "fetch.body",
        data,
        type: "error",
      });
    }
  }

  // Most likely Axios calls from tslib
  if (hint.xhr instanceof XMLHttpRequest) {
    const {status} = hint.xhr;
    if (status < 200 || 399 < status) {
      // eslint-disable-next-line no-underscore-dangle
      const sentryTempData = hint.xhr.__sentry_xhr_v3__;
      let body: Record<string, any> | string | undefined;
      if (sentryTempData?.method !== "GET") {
        const isFormData = sentryTempData?.request_headers["content-type"] === "application/x-www-form-urlencoded";
        const sentryTempBody = sentryTempData?.body;
        if (sentryTempBody) {
          body = isFormData ? sentryTempBody : (JSON.parse(sentryTempBody as string) as Record<string, any>);
        }
      }
      Sentry.addBreadcrumb({
        level: "debug",
        category: "faulty.xhr.status",
        data: {
          response: authReplacer(
            typeof hint?.xhr?.response === "string"
              ? hint?.xhr?.response
              : JSON.stringify(hint?.xhr?.response, null, 2),
          ),
          status,
          body,
        },
        type: "error",
      });
    }
  }

  return breadcrumb;
};

/**
 * Just some filtering, to skip out some persisting 3rd party errors (PA/t.js)
 * @param event
 */
export const beforeSend = (event: Sentry.ErrorEvent): Sentry.ErrorEvent | null => {
  if (event.exception?.values && event.exception.values[0] && event.exception.values[0].value) {
    /* Check for an error caused by `t.js` (Particular Audience, injected by GTM) */
    if (
      [
        "Argument 1 ('target') to MutationObserver.observe must be an instance of Node",
        "MutationObserver.observe: Argument 1 is not an object" /* Firefox */,
      ].includes(event.exception.values[0].value)
    ) {
      // Just ignore that error
      return null;
    }

    // Most likely a page leave on a non-returned fetch
    if (
      ["Load failed", "Failed to fetch"].includes(event.exception.values[0].value) &&
      event.exception.values[0].type === "TypeError"
    ) {
      return null;
    }
  }
  event.user = {
    id: window.c_UserUuidOrig,
  };

  return event;
};

/**
 * capture exception in sentry with additional information
 * @param err
 * @param checkout
 * @param occurrence
 * @param additionalInfo
 */
export const captureSentryException = (
  err: ApiError | IBraintreeError | Error,
  checkout: Checkout,
  occurrence: string,
  additionalInfo = {},
): string => {
  additionalInfo = {...additionalInfo, logisticsType: checkout.logisticsTypeId, paymentType: checkout.paymentId};

  // delete default toJSON method to get plain object
  delete (err as {toJSON?: () => {[key: string]: unknown}}).toJSON;
  // stringify error by using replacer method
  const errString = authReplacer(JSON.stringify(err, replacer, 2));
  // See `publishIncludesNavigation.xsl`
  const isCarder =
    checkout.paymentId === Payments.CreditCardBraintree &&
    document.body.querySelector<HTMLUListElement>("ul.main-nav-list")?.dataset.trackSntr ===
      checkout.cartData.value.payable.amount;

  return Sentry.captureException(err, {
    extra: {
      ...getAllMessages(checkout),
      additionalInfo,
      occurrence,
      errorDetails: errString,
      checkoutPaymentTransaction: authReplacer(JSON.stringify(checkout.paymentTransaction, null, 2)),
    },
    tags: {
      occurrence: isCarder ? "cca" : undefined,
    },
  });
};

/**
 * default JSON.stringify replacer method.
 * deletes/replaces some security relevant fields, that would otherwise lead to filtering in sentry
 * @param key
 * @param value just any json valid data. type safety here only for linter satisfactory
 */
const replacer = (key, value: string | undefined | {withCredentials?: boolean; withCredenteals?: boolean}) => {
  // just replace dom elements with simple string, to avoid circular structures
  if (value instanceof HTMLElement) {
    return "[HTMLElement]";
  }

  // replace bearer token with respective string, slightly modified to obey some PII rules
  if (typeof value === "string" && value.startsWith("Bearer ")) return "[Be4rerToken]";

  // undefineds will be deleted, so for informational reasons keep them with string 'undefined'
  if (value === undefined) return "undefined";

  // return value as is
  return value;
};

/**
 * extract all message nodes from object
 * @param obj
 */
const getAllMessages = (obj: Record<string, any>): {[key: string]: ICartMessage[]} => {
  let msgObject: Record<string, ICartMessage[]> = {};
  try {
    Object.entries<{messages: ICartMessage[]; text: string}>(obj).forEach(([entryKey, entryValue]) => {
      if (entryValue?.messages?.length) {
        /* if key has only decimals (array index, as in cart.positions[])
         * and position.text is set, take position.text as string key
         */
        const objKey = "MSGS_" + (!/\D/.test(entryKey) && entryValue.text ? entryValue.text : entryKey);
        msgObject[objKey] = entryValue.messages.map((msg: ICartMessage) => ({
          field: msg.field,
          type: msg.type,
          code: authReplacer(msg.code),
          message: authReplacer(msg.message),
        }));
      }

      if (
        entryKey !== "messages" &&
        typeof entryValue === "object" &&
        entryValue !== null &&
        !(entryValue instanceof HTMLElement)
      ) {
        msgObject = {...msgObject, ...getAllMessages(entryValue)};
      }
    });
  } catch (e) {
    return {
      ...msgObject,
      internalError: [{code: "internal", type: "error", field: "unknown", message: (e as Error).message}],
    };
  }

  return msgObject;
};

/**
 * general add breadcrumb function including some meta information
 * @param step
 * @param checkout
 * @param reason
 * @param shippings
 * @param customer
 */
export const sentryAddBreadcrumb = (
  step: CheckoutStep | "opening cart" | "closing cart",
  checkout: Checkout | null,
  reason: string,
  shippings: IGetCheckoutShippingOptionsData[] | undefined,
  customer: Customer,
): void => {
  let checkoutData: CheckoutMetaDataType | undefined;
  if (checkout) {
    checkoutData = {
      ...checkout.paymentTransaction,
      logisticsType: checkout.logisticsTypeId,
      paymentType: checkout.paymentId,
    };
  }

  Sentry.addBreadcrumb({
    category: "step.change",
    level: "info",
    message: step,
    type: "user",
    data: {
      customerStatus: customer.isLoggedIn ? "logged in" : customer.isRecognised ? "recognized" : "new",
      checkoutData,
      reason,
      shippings: shippings ? shippings.map((so) => so.logisticsTypeId) : [],
    },
  });
};

/**
 * breadcrumb function particularly for the payment transactions
 * @param transactionCategory
 * @param checkout checkout object **_before_** the transactions are called
 * @param additionalData
 */
export const sentryAddPaymentBreadcrumb = <T>(
  transactionCategory: TransactionCategoryType,
  checkout: Checkout,
  additionalData: T,
): void => {
  const additional: T & {[key: string]: ICartMessage | IPaymentTransaction | null} = {
    ...additionalData,
    ...getAllMessages(checkout),
    checkoutPaymentTransaction: checkout.paymentTransaction,
  };
  Sentry.addBreadcrumb({
    category: transactionCategory,
    level: "info",
    type: "transaction",
    data: additional,
  });
};

export const authReplacer = (input: string): string => {
  return input
    .replace(/auth/gi, "auTT")
    .replace(/token/gi, "T0KEN")
    .replace(/withCredentials/g, "withCredenteals");
};
